// Phase 2.11 Tier 2 — handoff completeness guard.
//
// Wraps the "Mark handed over" button on the asset detail page. When the
// warranty is incomplete (missing start date or provider email) the guard
// pops a styled confirmation modal explaining the consequence, with
// Cancel / Continue anyway. Continue submits the original form; Cancel
// leaves the user on the page so they can fix the missing fields.
//
// CLAUDE.md "Confirmation dialogs: never use browser confirm()" — this is
// a real modal with a backdrop, title, message, and two buttons. The
// modal is rendered into document.body so it survives turbo navigations
// and avoids z-index battles with the right action rail.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    blocked: Boolean,
    allowOverride: { type: Boolean, default: true },
    title: { type: String, default: "Warranty fields are missing" },
    message: {
      type: String,
      default:
        "Warranty start date and provider email are required for the FM team to file claims. Continuing without them undermines the handoff."
    }
  }

  connect() {
    this.element.addEventListener("submit", this._onSubmit)
  }

  disconnect() {
    this.element.removeEventListener("submit", this._onSubmit)
  }

  _onSubmit = (event) => {
    if (!this.blockedValue) return
    if (event.target.dataset.handoffConfirmed === "1") return

    event.preventDefault()
    this._showModal()
  }

  _showModal() {
    const overlay = document.createElement("div")
    overlay.className = "fixed inset-0 z-[60] flex items-center justify-center bg-black/50"

    const panel = document.createElement("div")
    panel.className = "bg-white rounded-lg shadow-dropdown w-full max-w-md mx-4"
    const overrideButton = this.allowOverrideValue ? `
      <button type="button" data-role="confirm"
              class="px-3 py-1.5 text-[13px] font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg">
        Continue anyway
      </button>
    ` : ""

    panel.innerHTML = `
      <div class="px-5 py-3 border-b border-gray-200">
        <h3 class="text-base font-semibold text-gray-900">${this._escape(this.titleValue)}</h3>
      </div>
      <div class="px-5 py-4 text-[13px] text-gray-700">
        ${this._escape(this.messageValue)}
      </div>
      <div class="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
        <button type="button" data-role="cancel"
                class="px-3 py-1.5 text-[13px] font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg">
          ${this.allowOverrideValue ? "Cancel" : "Got it"}
        </button>
        ${overrideButton}
      </div>
    `

    overlay.appendChild(panel)
    document.body.appendChild(overlay)

    const close = () => overlay.remove()
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close() })
    panel.querySelector('[data-role="cancel"]').addEventListener("click", close)
    const confirmButton = panel.querySelector('[data-role="confirm"]')
    if (confirmButton) {
      confirmButton.addEventListener("click", () => {
        close()
        this.element.dataset.handoffConfirmed = "1"
        this.element.requestSubmit ? this.element.requestSubmit() : this.element.submit()
      })
    }
  }

  _escape(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
